/**
 * A pocket calculator for the sidebar. It reads the expressions Node would read, but it
 * parses them rather than evaluating them: an expression in a notes app can arrive by
 * paste out of someone else's note, and nothing typed in that box gets to run code here.
 */
export type Calculation =
  | { ok: true; value: number; text: string }
  | { ok: false; error: string };

const MAX_LENGTH = 500;

const CONSTANTS: Record<string, number> = {
  pi: Math.PI,
  tau: Math.PI * 2,
  e: Math.E,
  phi: (1 + Math.sqrt(5)) / 2,
  inf: Infinity,
  infinity: Infinity,
  nan: NaN,
  ln2: Math.LN2,
  ln10: Math.LN10,
  log2e: Math.LOG2E,
  log10e: Math.LOG10E,
  sqrt2: Math.SQRT2,
  sqrt1_2: Math.SQRT1_2,
};

const FUNCTIONS: Record<string, (...args: number[]) => number> = {
  abs: Math.abs, acos: Math.acos, acosh: Math.acosh, asin: Math.asin,
  asinh: Math.asinh, atan: Math.atan, atan2: Math.atan2, atanh: Math.atanh,
  cbrt: Math.cbrt, ceil: Math.ceil, clz32: Math.clz32, cos: Math.cos,
  cosh: Math.cosh, exp: Math.exp, expm1: Math.expm1, floor: Math.floor,
  fround: Math.fround, hypot: Math.hypot, imul: Math.imul, log: Math.log,
  log10: Math.log10, log1p: Math.log1p, log2: Math.log2, max: Math.max,
  min: Math.min, pow: Math.pow, round: Math.round, sign: Math.sign,
  sin: Math.sin, sinh: Math.sinh, sqrt: Math.sqrt, tan: Math.tan,
  tanh: Math.tanh, trunc: Math.trunc,
};

/** min, max and hypot all report an arity of two but take any number of arguments. */
const VARIADIC = new Set(["min", "max", "hypot"]);

// Longest first, so >>> is never read as >> followed by >.
const OPERATORS = [
  "**", ">>>", "<<", ">>", "+", "-", "*", "/", "%", "&", "|", "^", "~", "(", ")", ",",
];
// Loosest binding first. ** and the signs bind tighter than every level here.
const BINARY_LEVELS = [["|"], ["^"], ["&"], ["<<", ">>", ">>>"], ["+", "-"], ["*", "/", "%"]];
const SIGNS = ["-", "+", "~"];

const NUMBER =
  /^(?:0[xX][0-9a-fA-F][0-9a-fA-F_]*|0[bB][01][01_]*|0[oO][0-7][0-7_]*|(?:\d[\d_]*)?\.?\d[\d_]*(?:[eE][+-]?\d+)?)/;
// Dotted names are read whole so Math.sqrt resolves and window.alert fails as one name.
const NAME = /^[A-Za-z_$][A-Za-z0-9_$]*(?:\.[A-Za-z_$][A-Za-z0-9_$]*)*/;

type Token =
  | { kind: "number"; value: number }
  | { kind: "name"; name: string }
  | { kind: "op"; op: string };

class CalculatorError extends Error {}
function fail(message: string): never {
  throw new CalculatorError(message);
}

function tokenize(source: string): Token[] {
  const tokens: Token[] = [];
  let rest = source;
  while (rest.trim()) {
    rest = rest.trimStart();
    const number = NUMBER.exec(rest);
    if (number) {
      tokens.push({ kind: "number", value: Number(number[0].replace(/_/g, "")) });
      rest = rest.slice(number[0].length);
      continue;
    }
    const name = NAME.exec(rest);
    if (name) {
      tokens.push({ kind: "name", name: name[0] });
      rest = rest.slice(name[0].length);
      continue;
    }
    const op = OPERATORS.find((candidate) => rest.startsWith(candidate));
    if (!op) fail(`Cannot read "${rest[0]}".`);
    tokens.push({ kind: "op", op });
    rest = rest.slice(op.length);
  }
  return tokens;
}

const combine = (op: string, a: number, b: number): number => {
  switch (op) {
    case "+": return a + b;
    case "-": return a - b;
    case "*": return a * b;
    case "/": return a / b;
    case "%": return a % b;
    case "&": return a & b;
    case "|": return a | b;
    case "^": return a ^ b;
    case "<<": return a << b;
    case ">>": return a >> b;
    default: return a >>> b;
  }
};

function parse(tokens: Token[], answer: number | undefined): number {
  let index = 0;
  const peek = (): Token | undefined => tokens[index];
  const eat = (op: string): boolean => {
    const token = peek();
    if (token?.kind !== "op" || token.op !== op) return false;
    index++;
    return true;
  };
  const binary = (level: number): number => {
    const ops = BINARY_LEVELS[level];
    if (!ops) return sign();
    let left = binary(level + 1);
    for (;;) {
      const token = peek();
      if (token?.kind !== "op" || !ops.includes(token.op)) return left;
      index++;
      left = combine(token.op, left, binary(level + 1));
    }
  };
  const sign = (): number => {
    const token = peek();
    if (token?.kind !== "op" || !SIGNS.includes(token.op)) return power();
    index++;
    const operand = sign();
    return token.op === "-" ? -operand : token.op === "~" ? ~operand : operand;
  };
  // Right associative: 2 ** 3 ** 2 is 2 ** 9, and the exponent may carry its own sign.
  const power = (): number => {
    const base = primary();
    return eat("**") ? base ** sign() : base;
  };
  const primary = (): number => {
    const token = peek();
    if (!token) return fail("Unfinished expression.");
    index++;
    if (token.kind === "number") return token.value;
    if (token.kind === "name") return named(token.name);
    if (token.op !== "(") return fail(`"${token.op}" needs a number before it.`);
    const value = binary(0);
    if (!eat(")")) return fail("Add the closing bracket.");
    return value;
  };
  const named = (raw: string): number => {
    const lower = raw.toLowerCase();
    const name = lower.startsWith("math.") ? lower.slice(5) : lower;
    if (name === "ans") {
      if (answer === undefined) return fail("No answer to reuse yet.");
      return answer;
    }
    const fn = FUNCTIONS[name];
    if (fn) {
      if (!eat("(")) return fail(`Call it like ${name}(2).`);
      const args: number[] = [];
      if (!eat(")")) {
        do args.push(binary(0));
        while (eat(","));
        if (!eat(")")) return fail("Add the closing bracket.");
      }
      if (!args.length) return fail(`${name}() needs a number.`);
      if (!VARIADIC.has(name) && args.length !== fn.length)
        return fail(`${name} takes ${fn.length} number${fn.length === 1 ? "" : "s"}.`);
      return fn(...args);
    }
    const constant = CONSTANTS[name];
    if (constant !== undefined) return constant;
    return fail(`Unknown name: ${raw}.`);
  };
  const value = binary(0);
  const left = peek();
  if (left)
    return fail(
      left.kind === "number"
        ? `Put an operator before ${left.value}.`
        : left.kind === "name"
          ? `Put an operator before ${left.name}.`
          : `Nothing to do with "${left.op}".`,
    );
  return value;
}

/** Printed the way Node prints a number, so the answer reads as it would in a REPL. */
export const formatNumber = (value: number): string => String(value);

export function calculate(source: string, answer?: number): Calculation {
  const expression = source.trim();
  if (!expression) return { ok: false, error: "Type an expression." };
  if (expression.length > MAX_LENGTH) return { ok: false, error: "That is too long to work out." };
  try {
    const value = parse(tokenize(expression), answer);
    return { ok: true, value, text: formatNumber(value) };
  } catch (error) {
    return {
      ok: false,
      error: error instanceof CalculatorError ? error.message : "That does not work out.",
    };
  }
}
